import { useState, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard, FileText, Settings as SettingsIcon, Menu, X, RefreshCw,
  Search, Bell, ChevronDown, Crown, ArrowRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../hooks/useAuth";
import { ChromeContext, useChromeState } from "../hooks/useChrome";
import { getStoreInfo } from "../services/api";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, match: (p: string) => p === "/dashboard" },
  { to: "/forms", label: "Forms", icon: FileText, match: (p: string) => p.startsWith("/forms") },
  { to: "/plans", label: "Plans", icon: Crown, match: (p: string) => p === "/plans" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, match: (p: string) => p === "/settings" },
];

function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
}

function Sidebar({
  onNavigate,
  mobile,
  onClose,
  storeName,
  planName,
}: {
  onNavigate: (to: string) => void;
  mobile?: boolean;
  onClose?: () => void;
  storeName: string;
  planName: string;
}) {
  const location = useLocation();
  return (
    <div
      className={`flex flex-col h-full bg-sidebarBg transition-[width] duration-300 ease-in-out ${
        mobile ? "w-64" : "w-20 hover:w-64 group/sidebar border-r border-sidebarBorder/50 shadow-2xl shadow-black/20"
      }`}
    >
      <div className="flex items-center h-16 px-5 flex-shrink-0 relative overflow-hidden">
        <div className="flex items-center gap-3 w-full">
          <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shadow-sm -ml-0.5">
            <span className="text-lg font-bold text-white">M</span>
          </div>
          <div
            className={`flex items-center justify-between min-w-0 transition-all duration-300 overflow-hidden whitespace-nowrap ${
              mobile ? "w-full opacity-100" : "w-0 opacity-0 group-hover/sidebar:w-full group-hover/sidebar:opacity-100"
            }`}
          >
            <div className="min-w-0 leading-tight pr-2">
              <p className="text-sm font-bold text-white font-sora truncate">Mobimint</p>
              <p className="text-[11px] text-sidebarTextMuted font-sans truncate">Form Builder</p>
            </div>
            {mobile && (
              <button onClick={onClose} className="text-sidebarText hover:text-white flex-shrink-0 ml-auto">
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto overflow-x-hidden">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = item.match(location.pathname);
          return (
            <button
              key={item.to}
              onClick={() => onNavigate(item.to)}
              title={item.label}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
              className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium font-sans transition-colors z-0 ${
                active ? "text-white" : "text-sidebarText hover:bg-sidebarHover hover:text-white"
              }`}
            >
              {active && (
                <motion.div
                  layoutId={mobile ? "nav-active-mobile" : "nav-active-desktop"}
                  className="absolute inset-0 bg-brand-500 rounded-lg shadow-brand z-[-1]"
                  initial={false}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                />
              )}
              <div className="flex items-center justify-center flex-shrink-0">
                <Icon className="h-5 w-5 relative z-10" />
              </div>
              <div
                className={`whitespace-nowrap overflow-hidden transition-all duration-300 text-left ${
                  mobile
                    ? "opacity-100 max-w-[200px]"
                    : "opacity-0 max-w-0 group-hover/sidebar:opacity-100 group-hover/sidebar:max-w-[200px]"
                }`}
              >
                <span className="relative z-10">{item.label}</span>
              </div>
            </button>
          );
        })}
      </nav>

      <div className="px-3 pb-3 flex-shrink-0 overflow-hidden">
        <button
          onClick={() => onNavigate("/plans")}
          className={`w-full text-left rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 relative group overflow-hidden shadow-md transition-all duration-300 flex flex-col ${
            mobile ? "p-4 mb-3" : "p-3 group-hover/sidebar:p-4 mb-3"
          }`}
        >
          {/* Subtle gloss overlay */}
          <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/20 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%]" />

          <div
            className={`flex items-center w-full transition-all duration-300 ${
              mobile ? "mb-2" : "mb-0 group-hover/sidebar:mb-2"
            }`}
          >
            <Crown
              className={`h-6 w-6 text-white/90 relative z-10 flex-shrink-0 transition-all duration-300 ${
                mobile ? "" : "mx-auto group-hover/sidebar:mx-0"
              }`}
            />
          </div>

          <div
            className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out w-full ${
              mobile
                ? "grid-rows-[1fr] opacity-100"
                : "grid-rows-[0fr] opacity-0 group-hover/sidebar:grid-rows-[1fr] group-hover/sidebar:opacity-100"
            }`}
          >
            <div className="overflow-hidden">
              <p className="text-xs font-bold text-white font-sora mb-1 relative z-10 whitespace-nowrap">Go Pro</p>
              <p className="text-[11px] text-white/75 font-sans mb-3 leading-relaxed relative z-10 line-clamp-2 whitespace-normal">
                Unlock advanced features and grow your business.
              </p>
              <span className="flex items-center gap-1.5 text-[11px] font-semibold text-brand-700 bg-white rounded-lg px-3 py-1.5 w-fit font-sans relative z-10 group-hover:shadow-sm transition-shadow whitespace-nowrap">
                Upgrade Now <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </div>
        </button>
      </div>

      <div className="px-3 pb-4 pt-3 border-t border-sidebarBorder flex-shrink-0 overflow-hidden">
        <div className="flex items-center gap-3 px-1.5 py-1.5 hover:bg-sidebarHover rounded-lg transition-colors cursor-pointer w-full overflow-hidden">
          <div className="h-9 w-9 rounded-full bg-brand-500/20 border border-brand-500/30 flex items-center justify-center flex-shrink-0 ml-0.5">
            <span className="text-[11px] font-bold text-brand-100 font-sans">{initials(storeName)}</span>
          </div>
          <div
            className={`whitespace-nowrap overflow-hidden transition-all duration-300 ${
              mobile
                ? "opacity-100 max-w-[200px]"
                : "opacity-0 max-w-0 group-hover/sidebar:opacity-100 group-hover/sidebar:max-w-[200px]"
            }`}
          >
            <div className="min-w-0 leading-tight text-left">
              <p className="text-sm font-semibold text-white font-sans truncate">{storeName}</p>
              <p className="text-[11px] text-sidebarTextMuted font-sans truncate">{planName}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Topbar({ storeName }: { storeName: string }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("/forms");
  };

  return (
    <div className="hidden lg:flex items-center gap-4 h-16 px-6 bg-surface border-b border-ink-100 sticky top-0 z-20">
      {/* <form onSubmit={submitSearch} className="flex-1 max-w-md">
        <div className="flex items-center gap-2 h-9 px-3 rounded-md bg-canvas border border-transparent focus-within:border-brand-500 focus-within:ring-4 focus-within:ring-brand-500/15 focus-within:bg-surface transition-all">
          <Search className="h-4 w-4 text-ink-500 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search forms…"
            className="flex-1 bg-transparent text-sm text-ink-900 placeholder:text-ink-500 font-sans outline-none"
          />
          <kbd className="text-[10px] font-semibold text-ink-500 font-sans px-1.5 py-0.5 rounded bg-ink-100 flex-shrink-0">
            ⌘K
          </kbd>
        </div>
      </form> */}

      <div className="flex items-center gap-3 ml-auto flex-shrink-0">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-md hover:bg-ink-100 transition-colors"
        >
          <div className="h-7 w-7 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
            <span className="text-[11px] font-bold text-brand-700 font-sans">{initials(storeName)}</span>
          </div>
          <span className="text-xs font-medium text-ink-900 font-sans hidden xl:block max-w-[120px] truncate">
            {storeName}
          </span>
        </button>
      </div>
    </div>
  );
}

function AppShell() {
  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useAuth();
  const chrome = useChromeState();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const { data: storeInfo } = useQuery({
    queryKey: ["store-settings"],
    queryFn: () => getStoreInfo().then((r) => r.data.store),
    enabled: isAuthenticated,
  });

  const storeName: string = storeInfo?.store_name || storeInfo?.store_url?.replace(".myshopify.com", "") || "My Store";
  const planName: string = storeInfo?.current_plan_name || (storeInfo?.plan_status === "active" ? "Active Plan" : "Free Plan");

  const goTo = (to: string) => {
    navigate(to);
    setMobileNavOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-canvas">
        <RefreshCw className="h-6 w-6 animate-spin text-brand-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen bg-canvas">
        <p className="text-sm text-ink-500 font-sans">Authenticating with Shopify…</p>
      </div>
    );
  }

  return (
    <ChromeContext.Provider value={chrome}>
      <div className="min-h-screen bg-canvas font-sans">
        {!chrome.hidden && (
          <>
            {/* Desktop sidebar */}
            <div className="hidden lg:block lg:fixed lg:inset-y-0 lg:left-0 lg:z-30">
              <Sidebar onNavigate={goTo} storeName={storeName} planName={planName} />
            </div>

            {/* Mobile sidebar drawer */}
            {mobileNavOpen && (
              <div className="fixed inset-0 z-40 lg:hidden">
                <div className="absolute inset-0 bg-black/35" onClick={() => setMobileNavOpen(false)} />
                <div className="absolute inset-y-0 left-0">
                  <Sidebar onNavigate={goTo} mobile onClose={() => setMobileNavOpen(false)} storeName={storeName} planName={planName} />
                </div>
              </div>
            )}

            {/* Mobile topbar */}
            <div className="lg:hidden sticky top-0 z-20 flex items-center gap-3 h-14 px-4 bg-surface border-b border-ink-100">
              <button onClick={() => setMobileNavOpen(true)} className="text-ink-500 hover:text-ink-900">
                <Menu className="h-5 w-5" />
              </button>
              <span className="text-sm font-bold text-ink-900 font-sora">Mobimint Forms</span>
            </div>
          </>
        )}

        <div className={chrome.hidden ? "" : "lg:pl-20"}>
          {!chrome.hidden && <Topbar storeName={storeName} />}
          <Outlet />
        </div>
      </div>
    </ChromeContext.Provider>
  );
}

export default function AppLayout() {
  return <AppShell />;
}